'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../hooks';
import { useSession } from 'next-auth/react';
import { safeDataLayerPush } from '../../utils/safeDataLayerPush';

const redirectKey = 'sign_in_redirect';
const loginFlowFlagKey = 'login_flow_initiated';

export const AuthContext = React.createContext<
  | {
      initializing: boolean;
      authenticated: boolean;
      error: boolean;
      apps: string[];
      authType: string | undefined;
      setRedirect: (redirect: string) => void;
      getRedirect: () => string | null;
      clearRedirect: () => void;
      botPermission: boolean | null;
      salesforceAccess: boolean | null;
      userEmail: string | null;
      trackingData: any | null;
      isRedirecting: boolean;
      setIsRedirecting: (value: boolean) => void;
    }
  | undefined
>(undefined);

AuthContext.displayName = 'AuthContext';

function setRedirect(redirect: string) {
  window.sessionStorage.setItem(redirectKey, redirect);
}

function getRedirect(): string | null {
  try {
    return window.sessionStorage.getItem(redirectKey);
  } catch {
    return null;
  }
}

function clearRedirect() {
  return window.sessionStorage.removeItem(redirectKey);
}

// Utility to encode email + '||' + timestamp with base64
function encodeEmailWithTimestamp(email: string): string {
  const timestamp = Date.now().toString();
  const toEncode = email + '||' + timestamp;
  const encoded =
    typeof window !== 'undefined' && window.btoa
      ? window.btoa(toEncode)
      : Buffer.from(toEncode, 'utf-8').toString('base64');
  return encoded;
}

export function useAuthContext() {
  const auth = React.useContext(AuthContext);

  if (!auth) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }

  return auth;
}

export function AuthProvider({ children, auth }: { children: React.JSX.Element; auth?: string }) {
  // Make sure sure this is NOT commented out before pushing
  const [authenticated, setAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Useful for LOCAL chatbot testing.   Do NOT check in
  // const [authenticated, setAuthenticated] = useState(true);
  // const [userEmail, setUserEmail] = useState<string | null>('dev@lastrev.com');
  // =============================================================================

  const [error, setError] = useState(false);
  const [initializing, setInitializing] = useState(auth !== 'Okta');
  const [apps, setApps] = useState<string[]>([]);
  const [botPermission, setBotPermission] = useState<boolean | null>(null);
  const [salesforceAccess, setSalesforceAccess] = useState<boolean | null>(null);
  const [trackingData, setTrackingData] = useState<any | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Track previous authenticated state to detect login transitions
  const prevAuthenticatedRef = useRef<boolean | null>(null);
  const hasInitializedRef = useRef<boolean>(false);
  const wasActuallyLoggedOutRef = useRef<boolean>(false);

  if (auth !== 'Okta') {
    const { data, error: apiError } = useAuth();

    useEffect(() => {
      setInitializing(!data && !apiError);
      const apps = data?.data?.apps;
      setApps(apps);
      setAuthenticated(!!data);
      setError(!!apiError);
      if (data?.data?.email) {
        setUserEmail(data?.data?.email ?? null);
      }
      setTrackingData(data?.data);
    }, [data, apiError]);
    // reset auth status when changing from normal(okta) to superuser(internal)
    useEffect(() => {
      setInitializing(true);
      setAuthenticated(false);
    }, []);
  } else if (auth === 'Okta') {
    const session = useSession();
    useEffect(() => {
      // @ts-ignore
      setUserEmail(session?.data?.user?.email ?? null);
      setApps(session?.data?.groups ?? []);
      setTrackingData(session?.data);
      // For Okta, authenticated is true if session exists and has data
      setAuthenticated(!!session?.data);
      setInitializing(session.status === 'loading');
    }, [session]);
  }

  // Track when user is actually logged out (not just initializing)
  useEffect(() => {
    // Only track after initialization is complete
    if (initializing) {
      return;
    }

    // If user is not authenticated after initialization completes, mark as actually logged out
    if (!authenticated) {
      wasActuallyLoggedOutRef.current = true;
    }
  }, [initializing, authenticated]);

  // Reset redirecting flag when user becomes authenticated
  useEffect(() => {
    if (authenticated && isRedirecting) {
      setIsRedirecting(false);
    }
  }, [authenticated, isRedirecting, setIsRedirecting]);

  // Reset redirecting flag when auth finishes initializing
  // This ensures the flag is fresh for each page load. If AuthGuard needs to redirect, it will set it again.
  // We always reset it when auth finishes initializing to ensure clean state on each page load
  useEffect(() => {
    if (!initializing) {
      // Always reset to false when auth finishes initializing
      // AuthGuard will set it to true if a redirect is actually needed
      setIsRedirecting(false);
    }
  }, [initializing, authenticated, setIsRedirecting]);

  // Track authentication state changes to detect login events
  useEffect(() => {
    // Don't track anything while still initializing
    if (initializing) {
      return;
    }

    // Mark as initialized after first auth check completes
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      prevAuthenticatedRef.current = authenticated;

      // If user is already authenticated on first check, they weren't logged out
      if (authenticated) {
        wasActuallyLoggedOutRef.current = false;
      }
      return;
    }

    // Detect transition from not authenticated to authenticated (actual login)
    const wasAuthenticated = prevAuthenticatedRef.current;
    const isAuthenticated = authenticated;

    // Check if user was redirected from login flow
    // We check multiple signals:
    // 1. Login flow flag in sessionStorage (set when user is sent to login)
    // 2. Redirect URL still in sessionStorage (user just came back from login, before it's cleared)
    // 3. User is on auth_callback page (coming back from login)
    // 4. Referrer is from the auth domain
    const hasLoginFlowFlag =
      typeof window !== 'undefined' && window.sessionStorage.getItem(loginFlowFlagKey) === 'true';
    const hasRedirectUrl = typeof window !== 'undefined' && window.sessionStorage.getItem(redirectKey) !== null;
    const isOnAuthCallback = typeof window !== 'undefined' && window.location.pathname.includes('/auth_callback');
    const referrerIsAuthDomain =
      typeof window !== 'undefined' &&
      window.document.referrer &&
      (window.document.referrer.includes('auth') ||
        window.document.referrer.includes(process.env.USER_AUTH_DOMAIN || '') ||
        window.document.referrer.includes(process.env.SUPPORT_AUTH_DOMAIN || ''));

    const cameFromLoginFlow = hasLoginFlowFlag || hasRedirectUrl || isOnAuthCallback || referrerIsAuthDomain;

    // Only fire login event if:
    // 1. User was actually logged out (not just initializing) - this means they were unauthenticated after init
    // 2. User transitioned from false to true (state change)
    // 3. User has tracking data (auth completed)
    // 4. User came from login flow (login flow flag, redirect URL, auth_callback page, or auth domain referrer)
    if (wasActuallyLoggedOutRef.current && !wasAuthenticated && isAuthenticated && trackingData && cameFromLoginFlow) {
      // User was logged out, clicked protected page, was sent to login, and now is authenticated
      // This is a real login action
      safeDataLayerPush({
        event: 'user_logged_in',
        isCustomEvent: 1
      });

      // Reset the flags after firing
      wasActuallyLoggedOutRef.current = false;
      // Clear the login flow flag
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(loginFlowFlagKey);
      }
    }

    // Update previous state
    prevAuthenticatedRef.current = authenticated;
  }, [authenticated, trackingData, initializing]);

  // Push user tracking data to dataLayer when user logs in
  useEffect(() => {
    if (trackingData) {
      const dataLayerUpdate: any = {};

      // If userEmail is set, get the domain name without the top level domain and send in client_id field
      // e.g. test@domain.com -> domain, test@eng.domain.com -> eng.domain, test@eng.dev.domain.com -> eng.dev.domain
      let domain: string | undefined;
      if (userEmail) {
        const hostname = userEmail.split('@')[1];
        if (hostname) {
          const parts = hostname.split('.');
          domain = parts.length > 1 ? parts.slice(0, -1).join('.') : hostname;
        }
      }

      // Handle different auth types
      if (auth === 'Okta') {
        // For Okta: user ID is extracted from JWT token (uid) and added to session.user.id
        // client_id is extracted from JWT token (cid) and added to session.client_id
        // Note: We don't use email as fallback to avoid sending PII to GA4
        if (trackingData.user?.id) {
          dataLayerUpdate.user_id = domain ?? trackingData.user.id;
          dataLayerUpdate.c_user_id = trackingData.user.id;
        }
        if (trackingData.client_id) {
          dataLayerUpdate.client_id = domain ?? trackingData.client_id;
        }
      } else {
        // For non-Okta auth: user ID is in trackingData.userId
        if (trackingData.userId) {
          dataLayerUpdate.user_id = domain ?? trackingData.userId;
          dataLayerUpdate.c_user_id = trackingData.userId;
        }
        if (trackingData.client_id) {
          dataLayerUpdate.client_id = domain ?? trackingData.client_id;
        }
      }

      // Only push if we have at least one value to update
      if (Object.keys(dataLayerUpdate).length > 0) {
        safeDataLayerPush(dataLayerUpdate);
      }
    }
  }, [trackingData, userEmail, auth]);

  // Fetch botPermission and salesforceAccess after authentication
  useEffect(() => {
    if (authenticated && userEmail) {
      // Only use the encoded email+timestamp for the checkBotWhitelist API call
      const encoded = encodeEmailWithTimestamp(userEmail);
      fetch(`/api/checkBotWhitelist?u=${encodeURIComponent(encoded)}`)
        .then((res) => {
          if (!res.ok) {
            return res.json().then((errorData) => {
              console.error('checkBotWhitelist API error:', {
                status: res.status,
                statusText: res.statusText,
                error: errorData
              });
              throw new Error(`API Error: ${res.status} - ${errorData.error || errorData.details || 'Unknown error'}`);
            });
          }
          return res.json();
        })
        .then((data) => {
          const roles = Array.isArray(data.roles) ? data.roles : [];
          setBotPermission(roles.includes(0));
          safeDataLayerPush({ whitelist_chatbot: roles.includes(0) });

          setSalesforceAccess(roles.includes(1));
          safeDataLayerPush({ whitelist_salesforce: roles.includes(1) });
        })
        .catch((error) => {
          console.error('checkBotWhitelist fetch error:', error);
          setBotPermission(false);
          setSalesforceAccess(false);
        });
    } else {
      setBotPermission(null);
      setSalesforceAccess(null);
    }
  }, [authenticated, userEmail]);

  const value = {
    authenticated,
    error,
    initializing,
    apps,
    authType: auth,
    setRedirect,
    getRedirect,
    clearRedirect,
    botPermission,
    salesforceAccess,
    userEmail,
    trackingData,
    isRedirecting,
    setIsRedirecting
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
